import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@inceptionbg/ui-components";
import { FarmContext } from "../../AppContent";
import {
  getReport,
  downloadReportExcel,
  downloadReportPdf,
} from "../../repos/FIeldBookRepo";
import { Table } from "../../Components/CustomIUI/Table/Table";
import {
  IFieldEntryReportData,
  IFieldEntryReportFilters,
} from "../../types/IFieldBook";
import { SubSection } from "../../Components/SubSection";
import {
  expensesByCategoryColumns,
  createExpensesByCategoryData,
  createExpensesByCategoryTotals,
  laborDataColumns,
  createLaborDataData,
  productionResultsColumns,
  createProductionResultsData,
  createProductionResultsTotals,
  reportFilters,
} from "./FieldEntryReportData";
import { Loader } from "../../Elements/Loader";

export const FieldEntryReportPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<IFieldEntryReportData | null>(
    null
  );
  const [filters, setFilters] = useState<IFieldEntryReportFilters>({});
  const [appliedFilters, setAppliedFilters] =
    useState<IFieldEntryReportFilters>({});

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid;

  const handleGetReport = useCallback(
    (filters: IFieldEntryReportFilters) => {
      if (!farmUuid) return;
      setIsLoading(true);
      getReport({ ...filters, farmUuid })
        .then((data) => {
          setReportData(data);
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    },
    [farmUuid]
  );

  useEffect(() => {
    if (farmUuid) {
      setFilters({});
      setAppliedFilters({});
      handleGetReport({});
    }
  }, [farmUuid, handleGetReport]);

  const onSearch = useCallback(
    (search: IFieldEntryReportFilters) => {
      const { fieldObj, plantCultureObj, entryTypeObj, ...searchWithoutObjs } =
        search;
      setAppliedFilters(searchWithoutObjs);
      handleGetReport(searchWithoutObjs);
    },
    [handleGetReport]
  );

  const handleOnSearch = useCallback(
    (search: IFieldEntryReportFilters) => {
      onSearch(search);
    },
    [onSearch]
  );

  const handleSetFilters = useCallback(
    (search: IFieldEntryReportFilters) => {
      setFilters(search);
    },
    [setFilters]
  );

  const handleDownloadExcel = useCallback(() => {
    if (!farmUuid) return;
    setIsLoading(true);
    downloadReportExcel({ ...appliedFilters, farmUuid })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [appliedFilters, farmUuid]);

  const handleDownloadPdf = useCallback(() => {
    if (!farmUuid) return;
    setIsLoading(true);
    downloadReportPdf({ ...appliedFilters, farmUuid })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [appliedFilters, farmUuid]);

  return (
    <div>
      <div className="page-section">
        <h1>{t("FieldEntryReport")}</h1>
      </div>

      <div className="sub-section-content">
        <Table
          columns={[]}
          data={[]}
          hideFooter
          filterData={
            !isLoading
              ? {
                  columns: ["field", "plantCulture", "entryType", "period"],
                  filters: reportFilters(
                    filters,
                    handleSetFilters,
                    t,
                    farmUuid
                  ),
                  search: appliedFilters,
                  searchData: filters,
                  setSearchData: handleSetFilters,
                  onSubmit: handleOnSearch,
                }
              : undefined
          }
          className="hidden-table"
        />

        <SubSection title={t("ExpensesByCategory")} className="mb-5">
          <Loader isLoading={isLoading}>
            <Table
              columns={expensesByCategoryColumns(t)}
              data={createExpensesByCategoryData(reportData, t)}
              totals={createExpensesByCategoryTotals(reportData, t)}
              hideFooter
            />
          </Loader>
        </SubSection>

        <SubSection title={t("LaborData")} className="mb-5">
          <Loader isLoading={isLoading}>
            <Table
              columns={laborDataColumns(t)}
              data={createLaborDataData(reportData)}
              hideFooter
            />
          </Loader>
        </SubSection>

        <SubSection title={t("ProductionResults")}>
          <Loader isLoading={isLoading}>
            <Table
              columns={productionResultsColumns(t)}
              data={createProductionResultsData(reportData)}
              totals={createProductionResultsTotals(reportData, t)}
              hideFooter
            />
          </Loader>
        </SubSection>

        <div className="flex gap-2 justify-right mt-3">
          <Button
            label={t("ExportExcel")}
            size="s"
            outlined
            onClick={handleDownloadExcel}
            disabled={!reportData || isLoading}
          />
          <Button
            label={t("ExportPdf")}
            size="s"
            outlined
            onClick={handleDownloadPdf}
            disabled={!reportData || isLoading}
          />
        </div>
      </div>
    </div>
  );
};
